import React, { useState } from 'react';
import { Search, FileText, Globe, Plus, ChevronRight, Upload, Link as LinkIcon } from 'lucide-react';
export function SimplifiedKnowledgeBase() {
  const [searchQuery, setSearchQuery] = useState('');
  // In Use integrations
  const inUseIntegrations = [{
    id: 'files',
    name: 'Text Files',
    description: 'Upload files to import any text content inside.',
    icon: FileText,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    count: '12 files',
    status: 'active'
  }, {
    id: 'website',
    name: 'Website',
    description: 'Enter the main website URL to import all subpages.',
    icon: Globe,
    iconBg: 'bg-purple-100',
    iconColor: 'text-purple-600',
    count: '3 websites',
    status: 'active'
  }];
  // Not in use integrations
  const notInUseIntegrations = [{
    id: 'google-drive',
    name: 'Google Drive',
    description: 'Add your files and documents as knowledge for your AI agent.',
    icon: 'https://upload.wikimedia.org/wikipedia/commons/1/12/Google_Drive_icon_%282020%29.svg',
    iconBg: 'bg-gray-100',
    iconColor: 'text-gray-600',
    status: 'inactive',
    action: 'Connect'
  }];
  return <div className="flex flex-col h-full overflow-hidden" data-id="element-2326">
      {/* Header */}
      <div className="mb-6 flex-shrink-0" data-id="element-2327">
        <h1 className="text-3xl font-bold tracking-tight mb-2" data-id="element-2328">Knowledge</h1>
        <p className="text-gray-600" data-id="element-2329">
          Add articles and connect external platforms for your AI agent. The
          agent will reference this knowledge content while resolving user
          inquiries.
        </p>
      </div>

      {/* Search Bar */}
      <div className="mb-6 flex-shrink-0" data-id="element-2330">
        <div className="relative max-w-xl" data-id="element-2331">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-2332" />
          <input type="text" placeholder="Search knowledge base..." className="asika-input w-full pl-10" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2333" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto" data-id="element-2334">
        {/* In Use Section */}
        <div className="mb-8" data-id="element-2335">
          <h2 className="text-lg font-semibold mb-4 text-gray-700" data-id="element-2336">In use</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2337">
            {inUseIntegrations.map(integration => <div key={integration.id} className="asika-card p-6 hover:shadow-md transition-shadow cursor-pointer" data-id="element-2338">
                <div className={`h-12 w-12 rounded-xl ${integration.iconBg} flex items-center justify-center mb-4`} data-id="element-2339">
                  <integration.icon className={integration.iconColor} size={24} data-id="element-2340" />
                </div>
                <h3 className="font-semibold text-lg mb-2" data-id="element-2341">
                  {integration.name}
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-2342">
                  {integration.description}
                </p>
                <button className="flex items-center gap-1 text-sm font-medium text-purple-600 hover:text-purple-700" data-id="element-2343">
                  {integration.count}
                  <ChevronRight size={16} data-id="element-2344" />
                </button>
              </div>)}

            {/* Add New File Upload Card */}
            <div className="asika-card p-6 hover:shadow-md transition-shadow cursor-pointer border-2 border-dashed border-gray-300 hover:border-purple-400" data-id="element-2345">
              <div className="h-12 w-12 rounded-xl bg-gray-100 flex items-center justify-center mb-4" data-id="element-2346">
                <Upload className="text-gray-600" size={24} data-id="element-2347" />
              </div>
              <h3 className="font-semibold text-lg mb-2" data-id="element-2348">Upload Files</h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-2349">
                Add new text files or documents to your knowledge base.
              </p>
              <button className="flex items-center gap-1 text-sm font-medium text-purple-600 hover:text-purple-700" data-id="element-2350">
                <Plus size={16} data-id="element-2351" />
                Add files
              </button>
            </div>
          </div>
        </div>

        {/* Not in Use Section */}
        <div data-id="element-2352">
          <h2 className="text-lg font-semibold mb-4 text-gray-700" data-id="element-2353">
            Not in use
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2354">
            {notInUseIntegrations.map(integration => <div key={integration.id} className="asika-card p-6 hover:shadow-md transition-shadow" data-id="element-2355">
                <div className={`h-12 w-12 rounded-xl ${integration.iconBg} flex items-center justify-center mb-4`} data-id="element-2356">
                  {typeof integration.icon === 'string' ? <img src={integration.icon} alt={integration.name} className="h-6 w-6" data-id="element-2357" /> : <integration.icon className={integration.iconColor} size={24} data-id="element-2358" />}
                </div>
                <h3 className="font-semibold text-lg mb-2" data-id="element-2359">
                  {integration.name}
                </h3>
                <p className="text-sm text-gray-600 mb-4" data-id="element-2360">
                  {integration.description}
                </p>
                <button className="flex items-center gap-1 text-sm font-medium text-purple-600 hover:text-purple-700" data-id="element-2361">
                  <Plus size={16} data-id="element-2362" />
                  {integration.action}
                </button>
              </div>)}

            {/* Website URL Card */}
            <div className="asika-card p-6 hover:shadow-md transition-shadow" data-id="element-2363">
              <div className="h-12 w-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4" data-id="element-2364">
                <LinkIcon className="text-orange-600" size={24} data-id="element-2365" />
              </div>
              <h3 className="font-semibold text-lg mb-2" data-id="element-2366">Add Website</h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-2367">
                Enter a website URL to crawl and import content.
              </p>
              <button className="flex items-center gap-1 text-sm font-medium text-purple-600 hover:text-purple-700" data-id="element-2368">
                <Plus size={16} data-id="element-2369" />
                Add URL
              </button>
            </div>

            {/* Coming Soon Cards */}
            <div className="asika-card p-6 opacity-60 relative" data-id="element-2370">
              <div className="absolute top-4 right-4" data-id="element-2371">
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded" data-id="element-2372">
                  COMING SOON
                </span>
              </div>
              <div className="h-12 w-12 rounded-xl bg-green-100 flex items-center justify-center mb-4" data-id="element-2373">
                <FileText className="text-green-600" size={24} data-id="element-2374" />
              </div>
              <h3 className="font-semibold text-lg mb-2" data-id="element-2375">Notion</h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-2376">
                Add your notes and docs as knowledge for your agent.
              </p>
            </div>

            <div className="asika-card p-6 opacity-60 relative" data-id="element-2377">
              <div className="absolute top-4 right-4" data-id="element-2378">
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded" data-id="element-2379">
                  COMING SOON
                </span>
              </div>
              <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4" data-id="element-2380">
                <FileText className="text-blue-600" size={24} data-id="element-2381" />
              </div>
              <h3 className="font-semibold text-lg mb-2" data-id="element-2382">Confluence</h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-2383">
                Add your Confluence space as knowledge for your AI agent.
              </p>
            </div>

            <div className="asika-card p-6 opacity-60 relative" data-id="element-2384">
              <div className="absolute top-4 right-4" data-id="element-2385">
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded" data-id="element-2386">
                  COMING SOON
                </span>
              </div>
              <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center mb-4" data-id="element-2387">
                <FileText className="text-red-600" size={24} data-id="element-2388" />
              </div>
              <h3 className="font-semibold text-lg mb-2" data-id="element-2389">Salesforce</h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-2390">
                Add your Salesforce knowledge articles to train your AI agent.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}